import { useMutation, useQueryClient, useQuery } from 'react-query';
import { useOneProvisionStatus } from 'hooks/useOneProvision';
import OneAuthAPI, { OneAuthConnection } from 'services/API/OneAuthAPI';
import { useUser } from 'state/auth';

export const ONE_AUTH_CONNECTIONS_QUERY_KEY = 'one-auth-connections';

/**
 * Fetches the third-party integrations the given identity has connected via
 * One Auth. Disabled until an identity is available.
 */
export const useOneAuthConnections = (identity?: string) => {
  return useQuery(
    [ONE_AUTH_CONNECTIONS_QUERY_KEY, identity],
    async () => {
      const result = await OneAuthAPI.getConnections(identity as string);

      return result?.data?.rows ?? [];
    },
    {
      enabled: Boolean(identity),
      // CRM connections rarely change mid-session, so fetch once and reuse the
      // cache across pages (PDP, DDP, account). The connect/delete mutations
      // invalidate this key when the list actually changes.
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
};

/**
 * Resolves the current user's active CRM connection (the first one One returns),
 * gated behind a provisioned One workspace. Shared by the CRM push action and
 * the record-link widget so their visibility gating stays in sync.
 */
export const useActiveCrmConnection = (): {
  connection: OneAuthConnection | undefined;
  identity: string | undefined;
} => {
  const { user } = useUser();
  // One identity is the users-app profile id (what OneProject is keyed on),
  // matching the backend so status/push resolve the user's project connection.
  const identity = user?.profile_id ? String(user.profile_id) : undefined;

  const { data: provision } = useOneProvisionStatus(Boolean(identity));
  const isProvisioned = Boolean(provision?.provisioned);
  const { data: connections = [] } = useOneAuthConnections(
    isProvisioned ? identity : undefined,
  );

  return { connection: connections[0], identity };
};

/**
 * Deletes a One Auth connection and refreshes the connections list on success.
 */
export const useDeleteOneAuthConnection = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (connectionId: string) => OneAuthAPI.deleteConnection(connectionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ONE_AUTH_CONNECTIONS_QUERY_KEY]);
      },
    },
  );
};
